/**
 * Paddle default payment-link page.
 *
 * This route is intentionally separate from /checkout, which remains the
 * existing PayTR iframe checkout used for Türkiye payments.
 *
 * Paddle appends ?_ptxn=txn_... to the default payment link. Once Paddle.js
 * is initialized, it detects that parameter and opens the matching checkout
 * automatically.
 *
 * Required production environment variable:
 *   REACT_APP_PADDLE_CLIENT_TOKEN=live_...
 *
 * Optional for sandbox-only testing:
 *   REACT_APP_PADDLE_ENV=sandbox
 */
import { useEffect, useState } from 'react';
import { ShieldCheck, Lock, Loader2 } from 'lucide-react';
import { getPaddle } from '@/lib/paddle';

// Aynı Paddle hesabı Surprivo kartlarını da satıyor. Varsayılan ödeme linki bu
// sayfa olduğu için Surprivo'nun terk edilen ödeme e-postaları da buraya düşer.
// Böyle bir ödeme açılınca müşteriyi Surprivo'nun kendi ödeme sayfasına gönderiyoruz.
const SURPRIVO_PAY_URL = 'https://surprivo.com/pay';

export const PaddleCheckout = () => {
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const onPaddleEvent = (e) => {
      const event = e?.detail;
      if (event?.name !== 'checkout.loaded') return;
      const app = event?.data?.custom_data?.app;
      const txn = event?.data?.transaction_id || new URLSearchParams(window.location.search).get('_ptxn');
      if (app === 'surprivo' && txn && /^txn_[a-z0-9]+$/i.test(txn)) {
        setStatus('redirecting');
        try { window.Paddle?.Checkout?.close(); } catch (err) { /* yok say */ }
        window.location.replace(`${SURPRIVO_PAY_URL}?_ptxn=${encodeURIComponent(txn)}`);
      }
    };
    window.addEventListener('paddle:event', onPaddleEvent);

    const initialize = async () => {
      try {
        await getPaddle();
        if (!cancelled) setStatus('ready');
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setError(err?.message || 'Secure checkout could not be loaded.');
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
      window.removeEventListener('paddle:event', onPaddleEvent);
    };
  }, []);

  const hasTransaction = new URLSearchParams(window.location.search).has('_ptxn');

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6 py-12" data-testid="paddle-checkout-page">
      <div className="w-full max-w-lg">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-[0_8px_30px_rgba(15,23,42,0.06)] p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EFF6FF] flex items-center justify-center mx-auto mb-5">
            <ShieldCheck className="w-7 h-7 text-[#2563EB]" />
          </div>

          <h1 className="text-2xl font-bold text-[#0F172A] mb-2">PaydaAI Secure Checkout</h1>
          <p className="text-sm text-[#64748B] leading-relaxed mb-6">
            Your payment is processed securely by Paddle. PaydaAI does not store your card details.
          </p>

          {status === 'loading' && (
            <div className="flex items-center justify-center gap-2 text-sm text-[#475569]" data-testid="paddle-loading">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading secure checkout...
            </div>
          )}

          {status === 'redirecting' && (
            <div className="flex items-center justify-center gap-2 text-sm text-[#475569]" data-testid="paddle-redirecting">
              <Loader2 className="w-4 h-4 animate-spin" />
              Redirecting to your payment...
            </div>
          )}

          {status === 'ready' && hasTransaction && (
            <div className="flex items-center justify-center gap-2 text-sm text-[#475569]" data-testid="paddle-ready">
              <Loader2 className="w-4 h-4 animate-spin" />
              Opening your payment...
            </div>
          )}

          {status === 'ready' && !hasTransaction && (
            <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] px-4 py-4 text-sm text-[#475569]" data-testid="paddle-no-transaction">
              This page is ready for secure Paddle payment links.
            </div>
          )}

          {status === 'error' && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-4 text-sm text-red-700" data-testid="paddle-error">
              {error}
            </div>
          )}

          <div className="mt-7 pt-5 border-t border-[#E2E8F0] flex items-center justify-center gap-2 text-xs text-[#94A3B8]">
            <Lock className="w-3.5 h-3.5" />
            Secure billing powered by Paddle
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaddleCheckout;
